import { z } from 'zod';
import { emailSchema, idSchema } from './common';
import type { TaskStatus } from './work';

/**
 * Portal klienta (DECISIONS 2026-10-17 [P] „Model portalu klienta”). Osoba klienta = członkostwo z rolą `client`
 * przypięte do jednego klienta (`memberships.client_id`). Po zalogowaniu w takim workspace'ie aplikacja pokazuje
 * wyłącznie portal (`/portal`): zgłoszenia jej klienta złożone w portalu, rozmowę i formularz nowego zgłoszenia.
 *
 * Bramka „domyślnie nie” (`@fos/core` `runService`, tRPC, trasy HTTP): osoba z rolą `client` wywołuje tylko usługi
 * portalu (`portal.*`, flaga `portal: true`) i usługi konta (sesja, hasło, wyloguj). Usługi portalu same filtrują po
 * `client_id` członkostwa; zgłoszenie innego klienta, zadanie spoza portalu i rekord innego workspace'u = NOT_FOUND.
 *
 * Zgłoszenie = zadanie (`origin = 'client_portal'`, status `request` → „Zgłoszenie”). DTO portalu nigdy nie zawiera
 * czasu, stawek, kwot, projektu, wykonawcy (najwyżej imię prowadzącego), komentarzy wewnętrznych ani historii.
 */

// ── Statusy po ludzku ───────────────────────────────────────────────────────
/** Status zgłoszenia widziany przez klienta. */
export const PORTAL_REQUEST_STATUSES = [
  'received',
  'planned',
  'in_progress',
  'on_hold',
  'to_check',
  'done',
  'closed',
] as const;
export type PortalRequestStatus = (typeof PORTAL_REQUEST_STATUSES)[number];

/** Etykiety statusów (frontend portalu używa tej samej listy). */
export const PORTAL_REQUEST_STATUS_LABELS: Record<PortalRequestStatus, string> = {
  received: 'Przyjęte do przeglądu',
  planned: 'Zaplanowane',
  in_progress: 'W realizacji',
  on_hold: 'Wstrzymane',
  to_check: 'Do sprawdzenia',
  done: 'Zakończone',
  closed: 'Zamknięte',
};

/** Odcień statusu (komponent Status w apps/web). */
export const PORTAL_REQUEST_STATUS_TONES: Record<PortalRequestStatus, 'neutral' | 'accent' | 'warning' | 'success'> = {
  received: 'accent',
  planned: 'neutral',
  in_progress: 'accent',
  on_hold: 'warning',
  to_check: 'warning',
  done: 'success',
  closed: 'neutral',
};

const STATUS_BY_TASK: Record<TaskStatus, PortalRequestStatus> = {
  request: 'received',
  todo: 'planned',
  in_progress: 'in_progress',
  blocked: 'on_hold',
  review: 'to_check',
  done: 'done',
};

/** Status zadania (i archiwum) → status zgłoszenia w portalu. Zarchiwizowane = „Zamknięte”. */
export function portalStatusOf(status: TaskStatus, archived: boolean): PortalRequestStatus {
  if (archived) return 'closed';
  return STATUS_BY_TASK[status];
}

/** Zakładki listy: „Otwarte” (wszystko przed „Zakończone”) i „Zamknięte” (zakończone i zamknięte). */
export const PORTAL_REQUEST_VIEWS = ['open', 'closed'] as const;
export type PortalRequestView = (typeof PORTAL_REQUEST_VIEWS)[number];
export const PORTAL_REQUEST_VIEW_LABELS: Record<PortalRequestView, string> = { open: 'Otwarte', closed: 'Zamknięte' };

export function isClosedPortalStatus(s: PortalRequestStatus): boolean {
  return s === 'done' || s === 'closed';
}

// ── Limity ──────────────────────────────────────────────────────────────────
export const PORTAL_TITLE_MAX = 200;
export const PORTAL_DESCRIPTION_MAX = 5000;
export const PORTAL_MESSAGE_MAX = 4000;
/** Najwięcej nowych zgłoszeń na osobę w ciągu doby (ochrona przed zalaniem tablicy). */
export const PORTAL_REQUESTS_PER_DAY = 30;
/** Najwięcej wiadomości osoby klienta w ciągu doby (wszystkie zgłoszenia). */
export const PORTAL_MESSAGES_PER_DAY = 200;
/** Lista portalu: najwięcej zgłoszeń w jednej zakładce. */
export const PORTAL_LIST_LIMIT = 200;

/** Ścieżki SPA portalu (link z maila do osoby klienta). */
export const PORTAL_WEB_PATHS = {
  home: '/portal',
  request: (id: string) => `/portal/zgloszenia/${id}`,
} as const;

export const PORTAL_MESSAGES = {
  forbidden: 'Ta funkcja nie jest dostępna w portalu klienta.',
  teamOnly: 'Ta funkcja jest dostępna tylko w portalu klienta.',
  dailyLimit: `Możesz dodać najwyżej ${PORTAL_REQUESTS_PER_DAY} zgłoszeń na dobę. Spróbuj jutro albo napisz do nas.`,
  messageLimit: 'Wysłano dziś zbyt wiele wiadomości. Spróbuj jutro.',
  cancelOnlyNew: 'Anulować można tylko zgłoszenie, którego zespół jeszcze nie zaplanował.',
  closedRequest: 'Zgłoszenie jest zamknięte. Dodaj nowe zgłoszenie.',
  cancelledNote: 'Zgłoszenie anulowane przez klienta.',
  // zespół: dostęp do portalu
  inviteClientArchived: 'Klient jest zarchiwizowany.',
  inviteContactNoEmail: 'Ten kontakt nie ma adresu email.',
  inviteTeamMember: 'Ten adres należy do osoby z zespołu. Osoba z zespołu nie może mieć dostępu do portalu.',
  inviteOtherClient: 'Ta osoba ma już dostęp do portalu innego klienta.',
  inviteAlreadyActive: 'Ta osoba ma już dostęp do portalu tego klienta.',
  invitePending: 'Zaproszenie dla tego adresu już czeka. Możesz wysłać je ponownie.',
  personNotFound: 'Nie znaleziono tej osoby w portalu klienta.',
  alreadyRevoked: 'Dostęp tej osoby do portalu jest już cofnięty.',
} as const;

// ── Wejścia (osoba klienta) ─────────────────────────────────────────────────
const requestTitleSchema = z
  .string()
  .trim()
  .min(3, { message: 'Tytuł musi mieć co najmniej 3 znaki' })
  .max(PORTAL_TITLE_MAX, { message: `Najwyżej ${PORTAL_TITLE_MAX} znaków` });
const requestDescriptionSchema = z
  .string()
  .trim()
  .max(PORTAL_DESCRIPTION_MAX, { message: `Najwyżej ${PORTAL_DESCRIPTION_MAX} znaków` });
const messageTextSchema = z
  .string()
  .trim()
  .min(1, { message: 'Wpisz treść wiadomości' })
  .max(PORTAL_MESSAGE_MAX, { message: `Najwyżej ${PORTAL_MESSAGE_MAX} znaków` });

export const portalMeInput = z.object({}).strict().optional();
export const portalListRequestsInput = z
  .object({ view: z.enum(PORTAL_REQUEST_VIEWS).default('open') })
  .strict()
  .default({});
export type PortalListRequestsInput = z.input<typeof portalListRequestsInput>;
export const portalGetRequestInput = z.object({ id: idSchema }).strict();
export const portalCreateRequestInput = z
  .object({
    title: requestTitleSchema,
    description: requestDescriptionSchema.default(''),
    /** „Pilne” → priorytet wysoki */
    urgent: z.boolean().default(false),
  })
  .strict();
export type PortalCreateRequestInput = z.input<typeof portalCreateRequestInput>;
export const portalAddMessageInput = z.object({ requestId: idSchema, text: messageTextSchema }).strict();
export type PortalAddMessageInput = z.input<typeof portalAddMessageInput>;
export const portalCancelRequestInput = z
  .object({
    id: idSchema,
    /** opcjonalny powód (trafia do rozmowy) */
    note: z.string().trim().max(1000, { message: 'Najwyżej 1000 znaków' }).optional(),
  })
  .strict();
export const portalUpdateProfileInput = z
  .object({
    name: z.string().trim().min(2, { message: 'Podaj imię i nazwisko' }).max(120, { message: 'Najwyżej 120 znaków' }),
  })
  .strict();

// ── DTO (osoba klienta) ─────────────────────────────────────────────────────
export interface PortalMe {
  workspace: { name: string };
  client: { name: string };
  person: { name: string; email: string; initials: string };
}

export interface PortalRequestSummary {
  id: string;
  /** numer zgłoszenia (#123 — numer zadania) */
  number: number;
  title: string;
  status: PortalRequestStatus;
  urgent: boolean;
  createdAt: Date;
  /** ostatnia zmiana: status, wiadomość albo edycja zgłoszenia */
  updatedAt: Date;
  messageCount: number;
}

export interface PortalMessageDto {
  id: string;
  author: 'team' | 'client';
  /** imię autora (zespół: samo imię; klient: imię i nazwisko) */
  authorName: string;
  /** wiadomość osoby, która czyta */
  mine: boolean;
  text: string;
  createdAt: Date;
}

export interface PortalRequestDetail extends PortalRequestSummary {
  description: string;
  /** imię osoby prowadzącej zgłoszenie (wykonawca zadania); null = jeszcze nikt */
  leadFirstName: string | null;
  /** anulowanie możliwe tylko przed przyjęciem (status „Przyjęte do przeglądu”) */
  canCancel: boolean;
  /** można pisać (zgłoszenie nie jest zamknięte) */
  canWrite: boolean;
  messages: PortalMessageDto[];
}

export interface PortalRequestList {
  items: PortalRequestSummary[];
  /** liczniki zakładek */
  counts: Record<PortalRequestView, number>;
  truncated: boolean;
}

// ── Zespół: dostęp do portalu (karta klienta → „Portal klienta”) ────────────
export const clientPortalListInput = z.object({ clientId: idSchema }).strict();
export const clientPortalInviteInput = z
  .object({
    clientId: idSchema,
    /** kontakt klienta (adres i imię z kontaktu) albo adres podany ręcznie */
    contactId: idSchema.optional(),
    email: emailSchema.optional(),
    name: z.string().trim().min(2, { message: 'Podaj imię i nazwisko' }).max(120).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (!v.contactId && !v.email) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['email'], message: 'Wybierz kontakt albo podaj adres email' });
    }
  });
export type ClientPortalInviteInput = z.input<typeof clientPortalInviteInput>;
export const clientPortalMemberInput = z.object({ membershipId: idSchema }).strict();
export const clientPortalInvitationInput = z.object({ invitationId: idSchema }).strict();

export interface ClientPortalPerson {
  membershipId: string;
  userId: string;
  name: string;
  email: string;
  status: 'active' | 'disabled';
  lastLoginAt: Date | null;
  createdAt: Date;
}

export interface ClientPortalInvitation {
  id: string;
  email: string;
  name: string | null;
  status: 'pending' | 'expired';
  expiresAt: Date;
  invitedBy: { userId: string; name: string } | null;
  createdAt: Date;
}

export interface ClientPortalAccess {
  clientId: string;
  people: ClientPortalPerson[];
  invitations: ClientPortalInvitation[];
  /** owner i admin (zarządzanie osobami) */
  canManage: boolean;
}
